import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errMsg } from "@/lib/useApi";
import { Copy, Loader2 } from "lucide-react";

export const SAMPLE = `FAMILY: UGC_NET
PAPER: Paper I
DIFFICULTY: easy
UNIT: Research Aptitude
SUBTOPIC: Types of research
Q: Which type of research aims to solve an immediate practical problem?
A) Fundamental research
B) Applied research
C) Historical research
D) Ex post facto research
ANSWER: B
EXPLANATION: Applied research is directed at solving a specific, practical problem.
---
FAMILY: CSIR_NET
PAPER: Part C
SUBJECT: 704
DIFFICULTY: medium
UNIT: Number theory
Q: Which of the following numbers are prime?
A) 2
B) 9
C) 17
D) 21
ANSWER: A, C
EXPLANATION: 2 and 17 have no divisors other than 1 and themselves.`;

const CLS = { valid: "border-emerald-200 bg-emerald-50 text-emerald-700", duplicate: "border-amber-200 bg-amber-50 text-amber-700", invalid: "border-red-200 bg-red-50 text-red-700" };

export default function BulkImport({ open, onOpenChange, onDone }) {
  const api = useApi();
  const [text, setText] = useState("");
  const [pv, setPv] = useState(null);
  const [include, setInclude] = useState({});
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState(null);

  const copy = async () => { try { await navigator.clipboard.writeText(SAMPLE); toast.success("Sample copied"); } catch { setText(SAMPLE); } };
  const preview = async () => {
    setBusy("preview"); setSummary(null);
    try {
      const r = await api.post("/admin/questions/bulk/preview", { text });
      setPv(r.data);
      setInclude(Object.fromEntries(r.data.items.map((it) => [it.index, it.status === "valid"])));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const doImport = async () => {
    const items = pv.items.filter((it) => include[it.index] && it.status !== "invalid").map((it) => it.data);
    if (!items.length) return toast.error("Nothing selected to import");
    setBusy("import");
    try {
      const r = await api.post("/admin/questions/bulk/import", { items, skip_duplicates: false });
      const excluded = pv.items.length - items.length;
      setSummary({ ...r.data, excluded, invalidTotal: pv.counts.invalid, dupTotal: pv.counts.duplicate });
      setPv(null); setText(""); onDone?.();
      toast.success(`${r.data.imported} question(s) imported as drafts`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const selected = pv ? pv.items.filter((it) => include[it.index] && it.status !== "invalid").length : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setPv(null); setSummary(null); } }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle>Bulk add questions</DialogTitle></DialogHeader>
        {!pv && !summary && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-2">
              <p>Separate questions with a line containing <code>---</code>. One field per line:</p>
              <p className="text-xs leading-relaxed"><code>FAMILY</code> UGC_NET | CSIR_NET · <code>PAPER</code> Paper I | Paper II (UGC) or Part A | Part B | Part C (CSIR) · <code>SUBJECT</code> code — required for Paper II / Part B / Part C ·
                <code> DIFFICULTY</code> easy | medium | hard · <code>UNIT</code>, <code>SUBTOPIC</code> optional · <code>Q</code> question · options <code>A)</code> … <code>F)</code> · <code>ANSWER</code> B or A, C (several letters = multiple-correct) · <code>EXPLANATION</code> optional · <code>SOURCE</code> optional.</p>
              <p className="text-xs">Paper I and Part A questions go to the shared pool once — they serve every subject automatically. Imported questions are saved as <b>drafts</b> and must be reviewed and published as usual.</p>
            </div>
            <div className="relative">
              <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap" data-testid="bulk-sample">{SAMPLE}</pre>
              <Button size="sm" variant="outline" onClick={copy} className="absolute top-2 right-2" data-testid="bulk-copy-sample"><Copy className="h-3.5 w-3.5 mr-1" />Copy sample</Button>
            </div>
            <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste your questions here…" className="font-mono text-xs" data-testid="bulk-textarea" />
            <DialogFooter><Button onClick={preview} disabled={!text.trim() || !!busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-preview-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Check & preview</Button></DialogFooter>
          </div>
        )}
        {pv && (
          <div className="space-y-4" data-testid="bulk-preview">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline" className={CLS.valid} data-testid="bulk-count-valid">{pv.counts.valid} valid</Badge>
              <Badge variant="outline" className={CLS.duplicate} data-testid="bulk-count-duplicate">{pv.counts.duplicate} duplicate</Badge>
              <Badge variant="outline" className={CLS.invalid} data-testid="bulk-count-invalid">{pv.counts.invalid} invalid</Badge>
            </div>
            <p className="text-xs text-slate-500">Invalid questions cannot be imported — fix them in the text and preview again. Duplicates are excluded unless you tick them.</p>
            <div className="space-y-2">
              {pv.items.map((it) => (
                <div key={it.index} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`bulk-item-${it.index}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox checked={!!include[it.index]} disabled={it.status === "invalid"} onCheckedChange={(v) => setInclude({ ...include, [it.index]: !!v })} data-testid={`bulk-include-${it.index}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">#{it.index}</span>
                        <Badge variant="outline" className={CLS[it.status]}>{it.status}</Badge>
                        {it.data.pool && <span className="text-xs text-slate-500">{it.data.pool === "SUBJECT" ? `${it.data.family} ${it.data.subject_code || "?"} · ${it.data.section || "Paper II"}` : `${it.data.pool} (shared)`} · {it.data.format}</span>}
                      </div>
                      <p className="mt-1 text-slate-800 truncate">{it.data.stem || "(no question text)"}</p>
                      {it.errors.map((e, i) => <p key={i} className="text-xs text-red-700 mt-1">Question {it.index}: {e}</p>)}
                      {it.batch_duplicate_of && <p className="text-xs text-amber-700 mt-1">Same as question #{it.batch_duplicate_of} in this batch.</p>}
                      {it.duplicates.map((d) => <p key={d.id} className="text-xs text-amber-700 mt-1">{Math.round(d.similarity * 100)}% similar to an existing {d.status} question: {d.stem.slice(0, 90)}</p>)}
                      {it.warnings.map((w, i) => <p key={i} className="text-xs text-slate-500 mt-1">{w}</p>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPv(null)} data-testid="bulk-back-btn">Back to edit</Button>
              <Button onClick={doImport} disabled={!selected || !!busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-import-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Import {selected} as drafts</Button>
            </DialogFooter>
          </div>
        )}
        {summary && (
          <div className="space-y-3 text-sm" data-testid="bulk-summary">
            <p className="font-display font-semibold text-lg">Import complete</p>
            <ul className="space-y-1">
              <li data-testid="bulk-summary-imported">Imported as drafts: <b>{summary.imported}</b></li>
              <li data-testid="bulk-summary-duplicate">Duplicates found: <b>{summary.dupTotal}</b></li>
              <li data-testid="bulk-summary-invalid">Invalid (not imported): <b>{summary.invalidTotal}</b></li>
              <li data-testid="bulk-summary-skipped">Skipped in total: <b>{summary.excluded + summary.skipped}</b></li>
            </ul>
            <p className="text-xs text-slate-500">Batch {summary.batch_id}. Review the drafts in the Question bank, then submit → approve → publish.</p>
            <DialogFooter><Button onClick={() => onOpenChange(false)} data-testid="bulk-close-btn">Close</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
